import { useSyncExternalStore } from "react";
import type { PostHog, PostHogConfig } from "posthog-js";

type FeatureFlagValue = boolean | string;
type FeatureFlags = Record<string, FeatureFlagValue>;
type PostHogModule = typeof import("posthog-js");
type QueuedCall = (client: PostHog) => void;

const MAX_QUEUED_CALLS = 100;

let posthogClient: PostHog | null = null;
let initialization: Promise<void> | null = null;
let queuedCalls: QueuedCall[] = [];
let featureFlags: FeatureFlags = {};
const featureFlagListeners = new Set<() => void>();

function persistenceKey(apiKey: string): string {
  const escapedKey = apiKey
    .replace(/\+/g, "PL")
    .replace(/\//g, "SL")
    .replace(/=/g, "EQ");
  return `ph_${escapedKey}_posthog`;
}

function readPersistedFeatureFlags(apiKey: string): FeatureFlags {
  try {
    const persisted = window.localStorage.getItem(persistenceKey(apiKey));
    if (!persisted) return {};

    const parsed: unknown = JSON.parse(persisted);
    if (typeof parsed !== "object" || parsed === null) return {};

    const enabled = (parsed as Record<string, unknown>)[
      "$enabled_feature_flags"
    ];
    if (typeof enabled !== "object" || enabled === null) return {};

    return Object.fromEntries(
      Object.entries(enabled).filter(
        (entry): entry is [string, FeatureFlagValue] =>
          typeof entry[1] === "boolean" || typeof entry[1] === "string",
      ),
    );
  } catch {
    return {};
  }
}

function replaceFeatureFlags(next: FeatureFlags): void {
  featureFlags = next;
  notifyFeatureFlagListeners();
}

function notifyFeatureFlagListeners(): void {
  for (const listener of featureFlagListeners) listener();
}

function queueOrRun(call: QueuedCall): void {
  if (posthogClient) {
    call(posthogClient);
    return;
  }

  if (queuedCalls.length === MAX_QUEUED_CALLS) queuedCalls.shift();
  queuedCalls.push(call);
}

function configurePostHogClient(
  client: PostHog,
  apiKey: string,
  config: Partial<PostHogConfig>,
): void {
  client.init(apiKey, config);
  posthogClient = client;
  // Hooks may already be rendering persisted values while the SDK chunk loads.
  // Re-read through the SDK now so it records the same exposure events as the
  // PostHog React hooks did before deferral.
  notifyFeatureFlagListeners();

  client.onFeatureFlags((flags, variants) => {
    replaceFeatureFlags(
      Object.fromEntries(flags.map((flag) => [flag, variants[flag] ?? true])),
    );
  });

  const pending = queuedCalls;
  queuedCalls = [];
  for (const call of pending) call(client);
}

/**
 * Starts loading PostHog without making it part of the entry's static import
 * closure. Calls made while the SDK chunk is loading are replayed in order.
 */
export function initializePostHog(
  apiKey: string,
  config: Partial<PostHogConfig>,
  load: () => Promise<PostHogModule> = () => import("posthog-js"),
): Promise<void> {
  if (initialization) return initialization;

  replaceFeatureFlags(readPersistedFeatureFlags(apiKey));
  initialization = load()
    .then((module) => {
      configurePostHogClient(module.default, apiKey, config);
    })
    .catch((error: unknown) => {
      console.error("Failed to initialize PostHog", error);
    });

  return initialization;
}

/** Keeps the installed mobile bundle's local SDK initialization synchronous. */
export function initializePostHogSynchronously(
  apiKey: string,
  config: Partial<PostHogConfig>,
  client: PostHog,
): void {
  if (initialization) return;
  replaceFeatureFlags(readPersistedFeatureFlags(apiKey));
  configurePostHogClient(client, apiKey, config);
  initialization = Promise.resolve();
}

export function identifyPostHogUser(
  ...args: Parameters<PostHog["identify"]>
): void {
  queueOrRun((client) => client.identify(...args));
}

export function resetPostHog(): void {
  queueOrRun((client) => client.reset());
}

export function registerPostHogProperties(
  ...args: Parameters<PostHog["register"]>
): void {
  queueOrRun((client) => client.register(...args));
}

export function capturePostHogEvent(
  ...args: Parameters<PostHog["capture"]>
): void {
  queueOrRun((client) => void client.capture(...args));
}

export function startPostHogSessionRecording(): void {
  queueOrRun((client) => client.startSessionRecording());
}

export function getPostHogSessionReplay(): {
  sessionId?: string;
  url?: string;
} {
  if (!posthogClient) return {};

  return {
    sessionId: posthogClient.get_session_id(),
    url: posthogClient.get_session_replay_url({ withTimestamp: true }),
  };
}

function subscribeToFeatureFlags(listener: () => void): () => void {
  featureFlagListeners.add(listener);
  return () => featureFlagListeners.delete(listener);
}

export function useFeatureFlagVariantKey(
  flag: string,
): FeatureFlagValue | undefined {
  return useSyncExternalStore(
    subscribeToFeatureFlags,
    () =>
      posthogClient ? posthogClient.getFeatureFlag(flag) : featureFlags[flag],
    () => undefined,
  );
}

/**
 * Non-React read of a feature flag, for boot-time gates that run before any
 * component mounts: the live client when initialized, else the snapshot
 * persisted by the previous session. `undefined` when the flag has never
 * been evaluated on this device (first run, or PostHog not initialized) —
 * callers choose their own default for that case.
 */
export function getFeatureFlagEnabled(flag: string): boolean | undefined {
  if (posthogClient) return posthogClient.isFeatureEnabled(flag);
  const value = featureFlags[flag];
  return value === undefined ? undefined : Boolean(value);
}

export function useFeatureFlagEnabled(flag: string): boolean | undefined {
  return useSyncExternalStore(
    subscribeToFeatureFlags,
    () => {
      if (posthogClient) return posthogClient.isFeatureEnabled(flag);
      const value = featureFlags[flag];
      return value === undefined ? undefined : Boolean(value);
    },
    () => undefined,
  );
}
